'use client';

import { ReactNode, memo } from 'react';
import dynamic from 'next/dynamic';
import TopNav from './TopNav';
import Header from './Header';

const Footer = dynamic(() => import('./Footer').catch(() => ({ default: () => null })), { ssr: false });
const ScrollToTop = dynamic(() => import('./ScrollToTop').catch(() => ({ default: () => null })), { ssr: false });

interface MainLayoutProps {
  children: ReactNode;
  showTopNav?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
}

function MainLayout({
  children,
  showTopNav = true,
  showHeader = true,
  showFooter = true,
}: MainLayoutProps) {
  return (
    <div className="min-h-screen bg-white dark:bg-dark-900 w-full overflow-x-hidden">
      <a href="#main-content" className="skip-to-main">
        Skip to main content
      </a>
      <div className="relative z-[200]">
        {showTopNav && <TopNav />}
        {showHeader && <Header />}
      </div>
      <main id="main-content" className="w-full">
        {/*
          Spacer = TopNav height + main nav height, both measured live into CSS
          vars by their components (--top-nav-height, --main-nav-height), so the
          spacer matches the fixed header exactly instead of over-reserving a
          hardcoded value and leaving a gap. Fallbacks cover first paint.
        */}
        <div
          className="md:hidden"
          style={{ height: 'calc(var(--top-nav-height, 56px) + var(--main-nav-height, 56px))' }}
          aria-hidden="true"
        />
        <div
          className="hidden md:block"
          style={{ height: 'calc(var(--top-nav-height, 0px) + var(--main-nav-height, 90px))' }}
          aria-hidden="true"
        />
        {children}
      </main>
      {showFooter && <Footer />}
      <ScrollToTop />
    </div>
  );
}

export default memo(MainLayout);
